/**
 * ConnectStatusLine — the persistent in-flight strip for a running connect
 * (2026-08-18 reliability doctrine): while the sign-in window is open the
 * user always sees where they are, an accidental close is recoverable via
 * [reopen] (same window name — never popup-blocked), and a poll timeout
 * resolves to a clear "didn't finish — try again" line instead of silence.
 */

import React from 'react';
import panelTheme from '../panelTheme';

export interface ConnectStatusLineProps {
  /** Display name of the app whose connect is in flight; null = none. */
  connectingApp: string | null;
  /** True once the sign-in window exists (mint finished). */
  canReopen: boolean;
  /** The timed-out app's display name; null = no timeout showing. */
  timedOutApp: string | null;
  onReopen: () => void;
  onCancel: () => void;
  onRetry: () => void;
  onDismissTimeout: () => void;
}

const strip: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
  padding: '0.6rem 1rem', marginBottom: '1rem',
  borderRadius: panelTheme.radiusInput, fontSize: '0.84rem',
};

const textButton: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0,
  fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
  textDecoration: 'underline', textTransform: 'lowercase',
};

export const ConnectStatusLine: React.FC<ConnectStatusLineProps> = ({
  connectingApp,
  canReopen,
  timedOutApp,
  onReopen,
  onCancel,
  onRetry,
  onDismissTimeout,
}) => {
  if (connectingApp) {
    return (
      <div
        data-testid="connect-status-line"
        style={{
          ...strip,
          background: panelTheme.surfaceGoldTint,
          border: `1px solid ${panelTheme.border}`,
          color: panelTheme.goldInk,
        }}
      >
        <span aria-hidden="true">⏳</span>
        <span style={{ flex: 1, minWidth: '200px' }}>
          waiting for you to finish in the <strong>{connectingApp}</strong> sign-in window…
        </span>
        {canReopen && (
          <button onClick={onReopen} data-testid="connect-status-reopen" style={{ ...textButton, color: panelTheme.goldInk }}>
            reopen
          </button>
        )}
        <button onClick={onCancel} data-testid="connect-status-cancel" style={{ ...textButton, color: panelTheme.grayText }}>
          cancel
        </button>
      </div>
    );
  }

  if (timedOutApp) {
    return (
      <div
        data-testid="connect-status-timeout"
        style={{
          ...strip,
          background: panelTheme.surface,
          border: `1px solid ${panelTheme.borderSoft}`,
          color: panelTheme.inkMuted,
        }}
      >
        <span style={{ flex: 1, minWidth: '200px' }}>
          the <strong>{timedOutApp}</strong> sign-in didn&rsquo;t finish — nothing changed.
        </span>
        <button onClick={onRetry} data-testid="connect-status-retry" style={{ ...textButton, color: panelTheme.goldInk }}>
          try again
        </button>
        <button onClick={onDismissTimeout} data-testid="connect-status-dismiss" style={{ ...textButton, color: panelTheme.grayText }}>
          dismiss
        </button>
      </div>
    );
  }

  return null;
};

export default ConnectStatusLine;
